import { css, html, LitElement, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property, query } from "lit/decorators.js";

import { repeat } from "lit/directives/repeat.js";
import { classMap } from "lit/directives/class-map.js";
import * as Styles from "../../styles.ts";
import { customEvent } from "../../util/event.ts";
import { type Ulid } from "../../../models/ulid.ts";
import * as Logger from "../../services/logger.ts";
import { type ChatMessage } from "../../../models/chat-message.ts";
import { type DuiScrollAreaElement } from "../dui/scroll-area/dui-scroll-area.ts";
import { type AiChatStore } from "./ai-chat-store.ts";
import { renderRoute } from "../../../util/route.ts";
import * as Config from "../../client-config.ts";
import { type ScenarioPhase } from "../../../tools/state/schema.ts";
import "../dui/scroll-area/dui-scroll-area.ts";
import "../dui/vstack/dui-vstack.ts";
import "./loading-text.ts";
import "./chat-message.ts";
import "../dui/spinner/dui-spinner.ts";
import "../dui/center/dui-center.ts";
import "../dui/alert-dialog/dui-alert-dialog.ts";
import "../dui/alert-dialog/dui-alert-dialog-popup.ts";
import "../dui/alert-dialog/dui-alert-dialog-title.ts";
import "../dui/alert-dialog/dui-alert-dialog-description.ts";
import "../dui/alert-dialog/dui-alert-dialog-close.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/menu/dui-menu.ts";
import "../dui/menu/dui-menu-item.ts";
import "../dui/breadcrumb/dui-breadcrumb.ts";
import "../dui/breadcrumb/dui-breadcrumb-list.ts";
import "../dui/breadcrumb/dui-breadcrumb-item.ts";
import "../dui/breadcrumb/dui-breadcrumb-link.ts";
import "../dui/breadcrumb/dui-breadcrumb-page.ts";
import "../dui/breadcrumb/dui-breadcrumb-separator.ts";
import "../dui/trunc/dui-trunc.ts";
import "../dui/hstack/dui-hstack.ts";
import "../dui/link/dui-link.ts";
import "./app-toolbar.ts";

const logger = Logger.create({ source: "client/ai-chat" });

const PHASES: ReadonlyArray<{ key: ScenarioPhase; label: string }> = [
  { key: "focal-question", label: "Focal Question" },
  { key: "scan-environment", label: "Scan Environment" },
  { key: "identify-forces", label: "Identify Forces" },
  { key: "identify-actors", label: "Identify Actors" },
  { key: "connect-forces", label: "Connect Forces" },
  { key: "map-forces", label: "Map Forces" },
  { key: "strategic-report", label: "Strategic Report" },
];

const styles = css`
  :host {
    display: block;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
    --paragraph-spacing: var(--space-5);
    --extra-margin-for-bordered-messages: var(--space-2);
  }

  chat-message:first-of-type:first-child {
    margin-top: 0;
  }

  .chat-loading {
    display: none;
    opacity: 0;
    pointer-events: none;
    transition:
      opacity var(--duration-normal) ease-in-out,
      display var(--duration-normal) ease-in-out allow-discrete;
  }

  .is-loading .chat-loading {
    display: block;
    opacity: 1;
    pointer-events: auto;
    @starting-style {
      opacity: 0;
    }
  }

  .chat-layout {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content 1fr min-content;
    grid-template-areas:
      "header"
      "main"
      "footer";
    height: 100cqh;
    width: 100cqw;
  }

  .chat-layout-header {
    grid-area: header;
    overflow: hidden;
  }

  .chat-layout-main {
    grid-area: main;
    overflow: hidden;
    position: relative;
  }

  .chat-layout-main > dui-scroll-area {
    height: 100%;
  }

  .phase-list {
    /* TODO: Temporarily hiding until we do layout properly */
    display: none !important;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: var(--space-7);
    z-index: 1;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .phase-row {
    position: relative;
    display: flex;
    align-items: center;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-snug);
    color: var(--muted-foreground);
    border-left: 2px solid var(--border);
    padding-left: var(--space-5);
    padding-top: var(--space-1);
    padding-bottom: var(--space-1);
  }

  .phase-row-icon-holder {
    position: absolute;
    left: 0;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--space-4);
    height: var(--space-4);
    transform: translate(-55%, -50%);
    --icon-size: var(--space-3_5);
    --icon-fg: transparent;
    background: color-mix(in oklch, var(--foreground) 5%, var(--background));
    outline: var(--border-width-medium) solid var(--background);
    border-radius: var(--radius-full);
  }

  .phase-row.completed .phase-row-icon {
    --icon-fg: var(--primary);
  }

  .phase-row.active {
    color: var(--primary);
    font-weight: var(--font-weight-medium);
    .phase-row-icon-holder {
      --icon-fg: var(--primary-foreground);
      background: color-mix(in oklch, var(--primary) 60%, var(--background));
    }
  }

  .chat-layout-footer {
    grid-area: footer;
    overflow: hidden;
  }

  .messages {
    display: flex;
    flex-direction: column;
    gap: var(--paragraph-spacing);
    padding: var(--space-2) var(--space-8) var(--space-6);
  }

  .measure {
    max-width: 42em;
    margin: 0 auto;
  }
`;

const MessageView = (
  message: ChatMessage,
  latestToolCallId: string | undefined,
  thinkingSummaries: string[] = [],
): TemplateResult => {
  return html`
    <chat-message
      .state="${message}"
      .latestToolCallId="${latestToolCallId}"
      .thinkingSummaries="${thinkingSummaries}"
    ></chat-message>
  `;
};

const LoadingView = (): TemplateResult => {
  return html`
    <dui-center>
      <dui-spinner size="md"></dui-spinner>
    </dui-center>
  `;
};

/** Request to toggle the canvas open/closed */
export const toggleCanvasEvent = customEvent<void>("toggle-canvas", {
  bubbles: true,
  composed: true,
});

/** Request to delete the current project */
export const requestDeleteConversationEvent = customEvent<Ulid>(
  "request-delete-conversation",
  {
    bubbles: true,
    composed: true,
  },
);

@customElement("ai-chat")
export class AIChatViewElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  /** Auth token for API calls */
  @property({ type: String })
  accessor accessToken: string | undefined = undefined;

  /** Scoped store from app-store */
  @property({ attribute: false })
  accessor store: AiChatStore | undefined = undefined;

  @property()
  accessor projectTitle: string = "";

  @property()
  accessor projectId: string | undefined;

  @property()
  accessor scenarioPhase: ScenarioPhase | undefined = undefined;

  @property({ type: Boolean, attribute: "canvas-open" })
  accessor canvasOpen: boolean = false;

  @query("#scroller", false)
  accessor #scroller: DuiScrollAreaElement | null = null;

  #lastSeenMessagesUpdate = 0;
  #shouldAutoScroll = true;

  override updated(): void {
    if (this.store) {
      const { messagesLastUpdated } = this.store.get();
      if (messagesLastUpdated > this.#lastSeenMessagesUpdate) {
        if (this.#shouldAutoScroll) {
          requestAnimationFrame(() => {
            if (this.#shouldAutoScroll) {
              this.scrollToBottom();
            }
          });
        }
        this.#lastSeenMessagesUpdate = messagesLastUpdated;
      }
    }
  }

  get #projectHref(): string {
    return this.projectId
      ? renderRoute(Config.PROJECT_URL, { project: this.projectId })
      : "#";
  }

  #onScrolledFromBottom = () => {
    this.#shouldAutoScroll = false;
  };

  #onScrolledToBottom = () => {
    this.#shouldAutoScroll = true;
  };

  #onClickDeleteConversation = () => {
    const conversationId = this.store?.get().conversationId;
    if (!conversationId) {
      logger.warn("Can't delete conversation. No conversation ID set.");
      return;
    }
    this.dispatchEvent(requestDeleteConversationEvent(conversationId));
  };

  #openDeleteConversationAlert = () => {
    this.store?.send({ type: "toggle-delete-alert", show: true });
  };

  #onDeleteAlertOpenChange = (event: CustomEvent<{ open: boolean }>) => {
    if (!event.detail.open) {
      this.store?.send({ type: "toggle-delete-alert", show: false });
    }
  };

  #onToggleCanvas = (): void => {
    this.dispatchEvent(toggleCanvasEvent());
  };

  #onSubmitInput = (event: CustomEvent<string>) => {
    this.#shouldAutoScroll = true;
    this.store?.send({
      type: "submit",
      content: event.detail,
      accessToken: this.accessToken,
    });
  };

  #onSelectChoice = (event: CustomEvent<{ value: string }>) => {
    this.#shouldAutoScroll = true;
    this.store?.send({
      type: "submit",
      content: event.detail.value,
      accessToken: this.accessToken,
    });
  };

  async scrollToBottom(): Promise<void> {
    await this.#scroller?.scrollToBottom();
  }

  override render(): TemplateResult {
    if (!this.store) {
      return LoadingView();
    }

    const {
      messages,
      isLoading,
      loadingText,
      showDeleteProjectAlert,
      conversationTitle,
      conversationId,
      latestToolCallId,
      thinkingSummaries,
    } = this.store.get();

    if (!conversationId) {
      return LoadingView();
    }

    const chatClasses = classMap({
      "ai-chat": true,
      "is-loading": isLoading,
    });

    return html`
      <div
        class="chat-layout ${chatClasses}"
        @value-change="${this.#onSelectChoice}"
        @submit-answers="${this.#onSelectChoice}"
      >
        <div class="chat-layout-header">
          <app-toolbar>
            <dui-breadcrumb>
              <dui-breadcrumb-list>
                <dui-breadcrumb-item>
                  <dui-breadcrumb-link>
                    <dui-link href="${this.#projectHref}"
                      ><dui-trunc max-width="30rem"
                        >${this.projectTitle}</dui-link
                    >
                  </dui-breadcrumb-link>
                </dui-breadcrumb-item>
                <dui-breadcrumb-separator></dui-breadcrumb-separator>
                <dui-breadcrumb-item>
                  <dui-breadcrumb-page>
                    <dui-menu>
                      <div slot="trigger">
                        <dui-hstack gap="0">
                          <dui-trunc max-width="100%"
                            >${conversationTitle}</dui-trunc
                          >
                          <dui-icon icon="arrow_drop_down"></dui-icon>
                        </dui-hstack>
                      </div>
                      <dui-menu-item
                        variant="danger"
                        @click="${this.#openDeleteConversationAlert}"
                        >Delete conversation</dui-menu-item
                      >
                    </dui-menu>
                  </dui-breadcrumb-page>
                </dui-breadcrumb-item>
              </dui-breadcrumb-list>
            </dui-breadcrumb>
            <dui-button
              slot="end"
              variant="ghost"
              size="icon-sm"
              square
              @click="${this.#onToggleCanvas}"
              aria-label="${this.canvasOpen ? "Close canvas" : "Open canvas"}"
            >
              <dui-icon
                icon="${this.canvasOpen ? "right_panel_close" : "right_panel_open"}"
                color="var(--muted-foreground)"
              ></dui-icon>
            </dui-button>
          </app-toolbar>
        </div>
        <div class="chat-layout-main">
          <dui-scroll-area
            id="scroller"
            fade
            @scrolled-from-bottom="${this.#onScrolledFromBottom}"
            @scrolled-to-bottom="${this.#onScrolledToBottom}"
          >
            <div class="messages measure">
              ${repeat(
                messages.values(),
                (message) => message.id,
                (message) =>
                  MessageView(
                    message,
                    latestToolCallId,
                    thinkingSummaries.get(message.id),
                  ),
              )}
              <loading-text
                class="chat-loading"
                .label="${loadingText}"
              ></loading-text>
            </div>
          </dui-scroll-area>
          <div class="phase-list">
            ${(() => {
              const activeIndex = PHASES.findIndex(
                (p) => p.key === this.scenarioPhase,
              );
              return PHASES.map((phase, phaseIndex) => {
                const isActive = phase.key === this.scenarioPhase;
                const isCompleted =
                  activeIndex >= 0 && phaseIndex < activeIndex;
                const rowClass = isActive
                  ? "active"
                  : isCompleted
                    ? "completed"
                    : "";
                const icon = isCompleted
                  ? "check"
                  : isActive
                    ? "keyboard_arrow_right"
                    : "";
                return html`
                  <div class="phase-row ${rowClass}">
                    <div class="phase-row-icon-holder">
                      <dui-icon
                        class="phase-row-icon"
                        icon="${icon}"
                      ></dui-icon>
                    </div>
                    ${phase.label}
                  </div>
                `;
              });
            })()}
          </div>
        </div>
        <div class="chat-layout-footer">
          <div class="measure">
            <ai-box
              class="measure"
              inset
              placeholder="Ask anything..."
              ?disabled="${isLoading}"
              focus-token="${conversationId}"
              @submit-input="${this.#onSubmitInput}"
            ></ai-box>
          </div>
        </div>
      </div>
      <dui-alert-dialog
        .open="${showDeleteProjectAlert}"
        @alert-dialog-open-change="${this.#onDeleteAlertOpenChange}"
      >
        <dui-alert-dialog-popup>
          <dui-alert-dialog-title>Delete conversation</dui-alert-dialog-title>
          <dui-alert-dialog-description>
            Are you sure you want to delete this&nbsp;conversation?
          </dui-alert-dialog-description>
          <dui-hstack gap="2">
            <dui-alert-dialog-close>
              <dui-button variant="outline">Cancel</dui-button>
            </dui-alert-dialog-close>
            <dui-alert-dialog-close>
              <dui-button
                variant="destructive"
                autofocus
                @click="${this.#onClickDeleteConversation}"
                >Delete</dui-button
              >
            </dui-alert-dialog-close>
          </dui-hstack>
        </dui-alert-dialog-popup>
      </dui-alert-dialog>
    `;
  }
}
